/**
 * Notification Bell Component
 * Displays notification icon with unread count badge
 */

import { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Divider,
  Box,
  Button,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNotifications } from '@/hooks/useNotifications';

/**
 * Format a timestamp as a relative time string
 */
const formatRelativeTime = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <SuccessIcon color="success" fontSize="small" />;
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />;
    case 'warning':
      return <WarningIcon color="warning" fontSize="small" />;
    case 'info':
    default:
      return <InfoIcon color="info" fontSize="small" />;
  }
};

export const NotificationBell: React.FC = () => {
  const { notifications, markRead, remove, clear } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notificationId: string) => {
    markRead(notificationId);
  };

  const handleRemove = (notificationId: string) => {
    remove(notificationId);
  };

  const handleClearAll = () => {
    clear();
    handleClose();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label={`${unreadCount} unread notifications`}
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.light',
            outlineOffset: 2,
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 480,
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" component="div">
            Notifications
          </Typography>
          {notifications.length > 0 && (
            <Button size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification.id)}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                opacity: notification.read ? 0.6 : 1,
              }}
            >
              {getNotificationIcon(notification.type)}
              <ListItemText
                primary={notification.message}
                secondary={
                  <>
                    {notification.description && (
                      <>
                        {notification.description}
                        {' \u00b7 '}
                      </>
                    )}
                    {formatRelativeTime(notification.timestamp)}
                  </>
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { fontWeight: notification.read ? 400 : 500 },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                }}
              />
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(notification.id);
                }}
                sx={{ minWidth: 'auto', px: 0.5, fontSize: '0.7rem' }}
              >
                Dismiss
              </Button>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};
